import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import googleSignup from './google-signup';
// routes

import { useRouter } from '../../routes/hooks';
// hooks

import { useBoolean } from '../../hooks/use-boolean';
// auth
import { useAuthContext } from '../../auth/hooks';
import Iconify from '../../components/iconify';

import FormProvider, { RHFTextField, RHFCheckbox } from '../../components/hook-form';
import RouterLink from '../../routes/components/router-link';
import { paths } from '../../routes/paths';
import { signInCognito } from '../../services/cognitoService';
import Auth from '../../services/auth';

// ----------------------------------------------------------------------

export default function LoginView() {
  const { login } = useAuthContext();

  const router = useRouter();

  const [errorMsg, setErrorMsg] = useState('');
  const [severity, setSeverity] = useState('error');

  const errorSignup = sessionStorage.getItem('error');

  if (errorSignup) {
    setErrorMsg(errorSignup);
    setSeverity('error');
    sessionStorage.removeItem('error');
  }

  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('El correo electrónico es requerido.')
      .email('El correo electrónico debe ser una dirección de correo electrónico válida.'),
    password: Yup.string().required('La contraseña es requerida.'),
  });

  const defaultValues = {
    email: '',
    password: '',
    remember: false,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const [capsLockActive, setCapsLockActive] = useState(false);

  useEffect(() => {
    const storedEmail = localStorage.getItem('rememberedEmail');
    const rememberChecked = localStorage.getItem('rememberChecked');
    if (rememberChecked === 'true' && storedEmail) {
      setValue('email', storedEmail);
      setValue('remember', true);
    }
  }, [setValue]);

  useEffect(() => {
    // check to see if the user was redirected from another view with an error message
    const redirected = JSON.parse(sessionStorage.getItem('redirected'));
    const redirectMessage = sessionStorage.getItem('redirectMessage');
    const redirectSeverity = sessionStorage.getItem('redirectSeverity');
    if (redirected === true && redirectMessage && redirectSeverity) {
      setErrorMsg(redirectMessage);
      setSeverity(redirectSeverity);
      sessionStorage.removeItem('redirected');
      sessionStorage.removeItem('redirectMessage');
      sessionStorage.removeItem('redirectSeverity');
    }
  }, [login]);

  const handleResponse = useCallback(
    async (response) => {
      googleSignup(response, router, setErrorMsg, login, setSeverity);
    },
    [router, login]
  );


  const onSubmit = handleSubmit(async (user) => {
    try {
      const token = await signInCognito(user.email, user.password);
      await login(token, user.email)
      router.push(paths.financial.root);
    } catch (error) {
      const errorCode = error.name;
      if (error.response?.status === 400 || errorCode === 'UserNotFoundException' || errorCode === 'NotAuthorizedException') {
        setErrorMsg('Credenciales inválidas.');
        setSeverity('error'); 
      } else if (errorCode === 'UserAlreadyAuthenticatedException') {
        setErrorMsg(`El usuario ya se encuentra logeado`);
        localStorage.clear();
      } else if (error === 'No current user') {
        setErrorMsg('Este usuario no existe. Asegúrese de haber validado su cuenta.');
        localStorage.clear();
      } else {
        setErrorMsg('Ocurrió un error inesperado, por favor inténtelo de nuevo.');
        setSeverity('error');
      }
    }
  });


  const renderHead = (
    <>
      <Typography variant="h4">Iniciar sesión</Typography>
      {!!errorMsg && severity && <Alert severity={severity}>{errorMsg}</Alert>}
    </>
  );

  const renderForm = (
    <>
      <RHFTextField name="email" label="Correo electrónico" autoFocus />
      <RHFTextField
        name="password"
        label="Contraseña"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onKeyDown={(e) => {
          setCapsLockActive(e.getModifierState('CapsLock'));
        }}
      />
      {capsLockActive && (
        <Alert
          key="warning"
          severity="warning"
          variant="outlined"
          onClose={() => {}}
          sx={{ width: 1 }}
        >
          Tienes activo las mayúsculas.
        </Alert>
      )}
      <RHFCheckbox name="remember" label="¿Recordar credenciales?" />
      <LoadingButton
        fullWidth
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Iniciar sesión
      </LoadingButton>
      <Link
        component={RouterLink}
        href={paths.auth.forgotPassword}
        variant="body2"
        color="inherit"
        underline="always"
        sx={{ alignSelf: 'flex-end' }}
      >
        ¿Has olvidado tu contraseña?
      </Link>
    </>
  );

  const renderFooter = (
    <Stack direction="row" spacing={0.5}>
      <Typography variant="body2">¿Nuevo usuario?</Typography>

      <Link component={RouterLink} href={paths.auth.signup} variant="subtitle2">
        Crea una cuenta
      </Link>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        {renderHead}
        {renderForm}
      </Stack>
    </FormProvider>
  );
}
